import _ from '@/utils/lodash-optimized'
import Issues from 'shared_src/igao/issues'
import IssueMapper from 'shared_src/mappings/issue'

import { getStore } from '../../lib/state.js'
import { uid } from '../igao/utils/index.js'
import {
  createIssues,
  showCloseableIssuesWarningModal
} from '../PatientFolder/IGAO/actions.js'

/**
 *  This method serves to define the data that is to be inserted into a dynamic issue description.
 *  The issue description dynamic fields provided by the issue mapping are defined using the respective
 *  form field data. The function returns the defined data in the form of:
 *    {someDynamicField: respectiveDynamicFieldData, ...}
 */

const defineDynamicIssueFields = (issue, currentForm) => {
  const definedData = {}
  const dynamicParameters = Object.keys(issue.dynamicParameters)

  for (let i = 0; i < dynamicParameters.length; i++) {
    if (!currentForm[issue.dynamicParameters[dynamicParameters[i]]]) {
      break
    }
    if (
      typeof currentForm[issue.dynamicParameters[dynamicParameters[i]]] ===
      'object'
    ) {
      definedData[dynamicParameters[i]] =
        currentForm[issue.dynamicParameters[dynamicParameters[i]]].display
    } else {
      definedData[dynamicParameters[i]] =
        currentForm[issue.dynamicParameters[dynamicParameters[i]]]
    }
  }

  return definedData
}

export default class IssueQueuer {
  // Initialize Issue Queuer
  constructor (o) {
    this.combinedForms = {}
    this.dispatch = o.dispatch
    this.issueQueue = []
    this.issueDequeue = []
    this.patientId = o.patientId
    this.practitionerId = o.practitionerId
    this.practitionerName = o.practitionerName
    this.encounterId = o.encounterId
    this.queueChanged = false
    this.deleteReference = o.deleteReference
    this.delete = o.delete
    this.callBackFn = o.callBackFn
  }
  /**
   *  This method serves to queueIssues. It is called on a form submission. It takes two parameters:
   *  an IGAO mapping file and a form. It loops through the mapping file and determines whether or not an issue should
   *  be queued, dependent upon the form data associated with its particluar IGAO mapping.
   */

  queueIssues (o, clientName) {
    this.queueChanged = false
    this.combinedForms = o.existingForms
      ? [ o.currentForm, ...o.existingForms ]
      : [ o.currentForm ]

    if (o.delete) {
      Object.keys(o.mapping)
        .forEach(field => {
          const igao = o.mapping[field]
          if (igao.unDocumented) {
            this.deleteFormIssues(igao.unDocumented, o.deleteReference)
          }
          if (igao.issues) {
            this.deleteFormIssues(igao, o.deleteReference)
          }
          if (igao.differentTriggerValues) {
            this.deleteFormIssues(igao.differentTriggerValues, o
              .deleteReference)
          }
          if (igao.needsTwoValues) {
            this.deleteFormIssues(igao.needsTwoValues, o.deleteReference)
          }
          // this.deleteFormIssues(igao, value, o.deleteReference)
        })
      this.returnIssues(clientName)
      return
    }

    Object.keys(o.mapping)
      .forEach(field => {
        const igao = o.mapping[field]
        const value = o.currentForm[field]
        // Check to see if an issue has a documentaiton issue associated with it. If it does, perform the necessary check.
        if (Array.isArray(value)) {
          const obj = value[0]
          Object.keys(igao)
            .forEach(key => {
              if (igao[key].unDocumented) {
                this.checkIfUndocumented(
                  igao[key].unDocumented,
                  obj[key],
                  o.currentForm
                )
              }
            })
        }
        if (igao.unDocumented) {
          this.checkIfUndocumented(igao.unDocumented, value, o.currentForm)
        }

        if (igao.anyValues) {
          this.checkForAnyDocumentation(this.combinedForms, igao.anyValues)
        }

        if (Object.prototype.hasOwnProperty.call(o.currentForm, field)) {
          // Check to see if the trigger value for an issue is equal to the form field value. If it is, queue the respective issue.
          // Otherwise dequeue the issue
          if (igao.issues) {
            this.checkIfIssueIsTriggered(igao, value, o.currentForm)
          }

          // If question can queue two or more different issues dependent on a different trigger value,
          // perform necessary check.
          if (igao.differentTriggerValues) {
            this.checkDifferentTriggerValues(
              igao.differentTriggerValues,
              value,
              o.currentForm
            )
          }

          if (igao.needsTwoValues) {
            this.needsTwoValues(igao.needsTwoValues, o.currentForm)
          }

          // Check and see if this value exists in any of the forms on this page
          if (igao.valueExists) {
            this.checkIfValueExists(field, igao.valueExists)
          }
        }
      })

    this.returnIssues(clientName)
  }

  /**
   *  This method serves to push all queued issues to state..
   */
  returnIssues (clientName) {
    const state = getStore()
      .getState()
    const hasCompartment =
      state.patient &&
      state.patient.compartment &&
      state.patient.compartment.CarePlan
    const existingCarePlans = Object.values(
      hasCompartment ? state.patient.compartment.CarePlan : {}
    )
      .filter(cp => ![ 'completed', 'cancelled' ].includes(cp.status))

      const currentUser = state?.users?.current_user
      const encounterId = state?.encounter?.encounter?.id

    const mapCurrentUser = {
      id: currentUser.id || this.practitionerId,
      display: currentUser.display || this.practitionerName
    }

    if (this.issueDequeue && this.issueDequeue.length > 0) {
      const dequeueIssueTitles = new Set(
        this.issueDequeue.map(it => {
          if (Issues.Issues[it.id]) {
            const issueForm = IssueMapper.mapIssueTemplateToForm(
              Issues.Issues[it.id],
              mapCurrentUser,
              this.patientId,
              it.definedData
            )

            return issueForm.description
          }
          return null
        })
      )

      const carePlanIds = existingCarePlans
        .filter(cp => dequeueIssueTitles.has(cp.description))
        .map(cp => cp.id)

      if (carePlanIds.length > 0) {
        this.dispatch(showCloseableIssuesWarningModal(carePlanIds))
      }
    }

    if (this.queueChanged) {
      this.alreadyCreatedIssues = this.alreadyCreatedIssues || []

      if ((state.patient && state.patient.compartment) || this.patientId) {
        const existingCarePlanTitles = new Set(
          existingCarePlans.map(cp => cp.description)
        )

        const carePlansAndGoals = this.issueQueue
          .map(it => {
            if (
              !this.alreadyCreatedIssues.includes(it.id) &&
              Issues.Issues[it.id]
            ) {
              return IssueMapper.mapFormToIssue({
                issue: IssueMapper.mapIssueTemplateToForm(
                  Issues.Issues[it.id],
                  mapCurrentUser,
                  this.patientId,
                  it.definedData
                ),
                currentUser,
                encounterId,
                uid
              })
            }
            return null
          })
          .filter(
            cp => cp && !existingCarePlanTitles.has(cp.issue.description)
          )

        this.alreadyCreatedIssues = this.alreadyCreatedIssues.concat(
          this.issueQueue.map(it => it.id)
        )

        if (carePlansAndGoals.length > 0) {
          this.dispatch(
            createIssues(carePlansAndGoals, this.patientId, clientName)
          )
        }
        if (this.callBackFn) {
          this.callBackFn()
        }
      } else {
        console.warn(
          'No patient compartment in the Redux store'
        )

        if (this.callBackFn) {
          this.callBackFn()
        }
      }
    }
    if (this.callBackFn) {
      this.callBackFn()
    }
  }

  /**
   *  This method serves to queue an issue associated with a particular IGAO mapping.
   */
  queueIssue (issue, definedData) {
    this.queueChanged = true
    this.issueQueue.push({
      id: issue.id,
      definedData
    })
  }

  /**
   *  This method serves to dequeue an issue associated with a particular IGAO mapping.
   */
  dequeueIssue (issue, definedData) {
    this.queueChanged = true
    this.issueDequeue.push({
      id: issue.id,
      definedData
    })
  }

  // This method checks to see if there are any values in grid list of form and triggers corresponding IGAO
  checkForAnyDocumentation (forms, igao) {
    const formCheck = !(forms.length < 2 && forms[0] === '')
    if (formCheck) {
      igao.issues.forEach(issue => this.dequeueIssue(issue))
    } else {
      igao.issues.forEach(issue => this.queueIssue(issue))
    }
  }

  /**
   *  This method serves to loop over all form entries and queue an issue if a form value is
   *  not present in any of the form entries.
   */
  checkIfValueExists (field, igao) {
    let valueExists = false
    const uniqueForms = _.uniqBy(this.combinedForms, (item) => item.id)

    for (let i = 0; i < uniqueForms.length; i++) {
      const form = uniqueForms[i]
        const value = form?.[field] ?? null

      if (value) {
        valueExists = true
        break
      }
    }

    igao.issues.forEach(issue => {
      if (valueExists) {
        this.dequeueIssue(issue)
      } else {
        this.queueIssue(issue)
      }
    })
  }

  needsTwoValues (igao, currentForm) {
    const value1 = currentForm[igao.field1.name]
    const value2 = currentForm[igao.field2.name]
    let trigger = false
    if (
      igao.field1.triggerValue === value1 &&
      igao.field2.triggerValue === value2
    ) {
      trigger = true
    }

    igao.issues.forEach(issue => {
      if (trigger) {
        this.queueIssue(issue)
      } else {
        this.dequeueIssue(issue)
      }
    })
  }

  /**
   *  This method serves to queue documentation issues. If a form field is not answered,
   *  then the appropriate documentation error issue is queued.
   */
  checkIfUndocumented (igao, value, currentForm) {
    // if documentation issue is dependent upon the answer to another field, that has multipul possible values.
    if (
      igao.isDependent &&
      currentForm[igao.dependency.field] &&
      Array.isArray(igao.dependency.triggerValue)
    ) {
      const triggerArray = igao.dependency.triggerValue
      const triggerIssue = []

      triggerArray.forEach(t => {
        if (t === currentForm[igao.dependency.field]) {
          triggerIssue.push('1')
        }
      })

      if (triggerIssue.length && !value) {
        for (let i = 0; i < igao.issues.length; i++) {
          const issue = igao.issues[i]
          let definedData

          if (issue.isDynamic) {
            definedData = defineDynamicIssueFields(issue, currentForm)
            if (!definedData) {
              continue
            }
          }
          this.queueIssue(issue, definedData)
        }
      }

      if (value || !triggerIssue.length) {
        for (let i = 0; i < igao.issues.length; i++) {
          const issue = igao.issues[i]
          let definedData

          if (issue.isDynamic) {
            definedData = defineDynamicIssueFields(issue, currentForm)
            if (!definedData) {
              continue
            }
          }
          this.dequeueIssue(issue, definedData)
        }
      }

      return
    }
    // if documentation issue is dependent upon the answer to another field, validate.
    if (
      igao.isDependent &&
      currentForm[igao.dependency.field] !== igao.dependency.triggerValue
    ) {
      for (let i = 0; i < igao.issues.length; i++) {
        const issue = igao.issues[i]
        let definedData

        if (issue.isDynamic) {
          definedData = defineDynamicIssueFields(issue, currentForm)
          if (!definedData) {
            continue
          }
        }
        this.dequeueIssue(issue, definedData)
      }

      return
    }

    for (let i = 0; i < igao.issues.length; i++) {
      const issue = igao.issues[i]
      let definedData

      if (issue.isDynamic) {
        definedData = defineDynamicIssueFields(issue, currentForm)
        if (!definedData) {
          continue
        }
      }
      if (value) {
        this.dequeueIssue(issue, definedData)
      } else {
        this.queueIssue(issue, definedData)
      }
    }
  }

  deleteFormIssues (igao, currentForm) {
    for (let i = 0; i < igao.issues.length; i++) {
      const issue = igao.issues[i]

      let definedData

      if (issue.isDynamic) {
        definedData = defineDynamicIssueFields(issue, currentForm)
        if (!definedData) {
          continue
        }
      }
      this.dequeueIssue(issue, definedData)
    }
  }

  /**
   *  This method serves to queue issues if they are triggered. If the value of the IGAO
   *  mapping is equal to the respective form field value, then queue the issue. Otherwise
   *  dequeue the issue. If the issue is to be queued and is dynamic, define the data for
   *  the dynamic paramaters.
   */
  checkIfIssueIsTriggered (igao, value, currentForm) {
    for (let i = 0; i < igao.issues.length; i++) {
      const issue = igao.issues[i]

      let definedData

      if (issue.isDynamic) {
        definedData = defineDynamicIssueFields(issue, currentForm)
        if (!definedData) {
          continue
        }
      }

      let isTriggered = false
      if (igao.triggerValues) {
        igao.triggerValues.forEach(triggerValue => {
          if (triggerValue === value) {
            isTriggered = true
          }
        })
      } else {
        isTriggered = igao.triggerValue === value
      }
      if (isTriggered) {
        this.queueIssue(issue, definedData)
      } else {
        this.dequeueIssue(issue, definedData)
      }
    }
  }

  /**
   *  This method serves to queue issues if they are triggered by different trigger values. If the value of the IGAO
   *  mapping trigger value is equal to the respective form field value, then queue the issue. Otherwise
   *  dequeue the issue. If the issue is to be queued and is dynamic, define the data for
   *  the dynamic paramaters.
   */
  checkDifferentTriggerValues (igao, value, currentForm) {
    for (let i = 0; i < igao.issues.length; i++) {
      const issue = igao.issues[i]

      let definedData

      if (issue.isDynamic) {
        definedData = defineDynamicIssueFields(issue, currentForm)
        if (!definedData) {
          continue
        }
      }

      let isTriggered = false
      if (igao.triggerValues) {
        igao.issues[i].triggerValues.forEach(triggerValue => {
          if (triggerValue === value) {
            isTriggered = true
          }
        })
      } else {
        isTriggered = igao.issues[i].triggerValue === value
      }
      if (isTriggered) {
        this.queueIssue(issue, definedData)
      } else {
        this.dequeueIssue(issue, definedData)
      }
    }
  }
}
